import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { confirm } from "../components/ConfirmDialog";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { SecurityPanel } from "../components/SecurityPanel";
import { User, Mail, Smartphone, KeyRound, LogOut, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * /account — self-service settings for the signed-in user.
 * Owner/Manager/Employee — any user can update their own name/photo, and
 * initiate an email or phone change (both require re-verification of the
 * new destination before the change lands).
 */
const AccountSettings = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailStep, setEmailStep] = useState("idle"); // idle | code
  const [emailRequestId, setEmailRequestId] = useState("");
  const [emailCode, setEmailCode] = useState("");

  const [newPhone, setNewPhone] = useState("");
  const [phoneStep, setPhoneStep] = useState("idle"); // idle | code
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneSubmitting, setPhoneSubmitting] = useState(false);

  const [signingOutAll, setSigningOutAll] = useState(false);

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user]);

  const saveName = async () => {
    if (!name.trim()) { toast.error("Name cannot be empty."); return; }
    setSavingName(true);
    try {
      const res = await apiClient.patch("/me/account", { name: name.trim() });
      if (setUser && res.data?.updated?.name) {
        setUser((u) => (u ? { ...u, name: res.data.updated.name } : u));
      }
      toast.success("Name updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update name.");
    } finally {
      setSavingName(false);
    }
  };

  const requestEmailChange = async () => {
    if (!newEmail.trim().includes("@")) { toast.error("Enter a valid email."); return; }
    setEmailSubmitting(true);
    try {
      const res = await apiClient.post("/me/email-change/request", { new_email: newEmail.trim().toLowerCase() });
      setEmailRequestId(res.data?.request_id || "");
      setEmailStep("code");
      toast.success("Verification code sent — check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start email change.");
    } finally {
      setEmailSubmitting(false);
    }
  };

  const verifyEmailChange = async () => {
    if (emailCode.trim().length !== 6) { toast.error("Enter the 6-digit code."); return; }
    if (!emailRequestId) { toast.error("Session expired — please request a new code."); return; }
    setEmailSubmitting(true);
    try {
      await apiClient.post("/me/email-change/verify", {
        request_id: emailRequestId,
        otp: emailCode.trim(),
      });
      toast.success("Email verified and saved.");
      if (setUser) setUser((u) => (u ? { ...u, email: newEmail.trim().toLowerCase() } : u));
      setEmailStep("idle");
      setEmailCode("");
      setEmailRequestId("");
      setNewEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed — check the code and try again.");
    } finally {
      setEmailSubmitting(false);
    }
  };

  const cancelEmailChange = () => {
    setEmailStep("idle");
    setEmailCode("");
    setEmailRequestId("");
  };

  const requestPhoneChange = async () => {
    if (!newPhone.trim()) { toast.error("Enter a phone number."); return; }
    setPhoneSubmitting(true);
    try {
      await apiClient.post("/me/phone/request-change", { new_phone: newPhone.trim() });
      setPhoneStep("code");
      toast.success("Verification code sent by SMS.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start phone change.");
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const verifyPhoneChange = async () => {
    if (phoneCode.length < 4) { toast.error("Enter the code from your SMS."); return; }
    setPhoneSubmitting(true);
    try {
      const res = await apiClient.post("/me/phone/verify-change", { code: phoneCode });
      if (setUser) setUser((u) => (u ? { ...u, phone: res.data.phone } : u));
      toast.success("Phone number updated. Any 'trust this device' cookies on the OLD device were revoked.");
      setPhoneStep("idle"); setNewPhone(""); setPhoneCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wrong code — please try again.");
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const signOutEverywhere = async () => {
    const ok = await confirm({
      title: "Sign out of every device?",
      description: "You'll be signed out here and on every browser, phone, or tablet where you're currently signed in — including trusted devices. You'll need to sign in again on each one.",
      confirmText: "Sign out everywhere",
      variant: "warning",
    });
    if (!ok) return;
    setSigningOutAll(true);
    try {
      await apiClient.post("/me/signout-everywhere");
      localStorage.removeItem("session_token");
      localStorage.removeItem("device_token");
      sessionStorage.setItem("just_signed_out", "1");
      toast.success("Signed out everywhere.");
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sign out everywhere.");
    } finally {
      setSigningOutAll(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="account-page">
        <div>
          <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope']">Account settings</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Signed in as <span className="font-medium">{user?.email || user?.phone || user?.name}</span>
            {user?.role && <> · {user.role}</>}
          </p>
        </div>

        {/* Name */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><User className="w-5 h-5 text-[#0D9488]" /> Name</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Label htmlFor="name">Display name</Label>
            <div className="flex gap-2">
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name-input" />
              <Button onClick={saveName} disabled={savingName || name === user?.name} data-testid="account-name-save" className="bg-[#0D9488] hover:bg-[#14B8A6]">
                <Save className="w-4 h-4 mr-2" />
                {savingName ? "Saving…" : "Save"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Email — reuse existing /me/email-change router */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#0D9488]" /> Email</CardTitle>
            <CardDescription>
              Current: <span className="font-medium">{user?.email || <em className="text-slate-400">not set</em>}</span>
              <br />
              We&apos;ll email a 6-digit code to the NEW address. Enter it below to confirm the change.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {emailStep === "idle" ? (
              <>
                <Label htmlFor="new-email">New email</Label>
                <div className="flex gap-2">
                  <Input id="new-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="new@example.com" data-testid="account-email-input" />
                  <Button onClick={requestEmailChange} disabled={emailSubmitting || !newEmail} data-testid="account-email-submit" className="bg-[#0D9488] hover:bg-[#14B8A6]">
                    {emailSubmitting ? "Sending…" : "Send code"}
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-3" data-testid="email-change-code-step">
                <p className="text-sm text-slate-600">Enter the 6-digit code we emailed to <strong>{newEmail}</strong>. It expires in 15 minutes.</p>
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="text-center tracking-widest text-lg"
                  data-testid="account-email-code-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={verifyEmailChange}
                    disabled={emailSubmitting || emailCode.length !== 6}
                    data-testid="account-email-verify"
                    className="flex-1 bg-[#0D9488] hover:bg-[#14B8A6]"
                  >
                    {emailSubmitting ? "Verifying…" : "Verify & save"}
                  </Button>
                  <Button variant="outline" onClick={cancelEmailChange} disabled={emailSubmitting}>Cancel</Button>
                </div>
                <button
                  type="button"
                  onClick={requestEmailChange}
                  disabled={emailSubmitting}
                  className="text-xs text-[#0D9488] hover:underline disabled:opacity-50"
                >
                  Didn&apos;t get the code? Resend
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Phone */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5 text-[#0D9488]" /> Phone</CardTitle>
            <CardDescription>
              Current: <span className="font-medium">{user?.phone || <em className="text-slate-400">not set</em>}</span>
              <br />
              We&apos;ll SMS a code to the NEW number. Trusted-device cookies on your OLD device get revoked when the change lands.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {phoneStep === "idle" ? (
              <>
                <Label htmlFor="new-phone">New phone (US)</Label>
                <div className="flex gap-2">
                  <Input id="new-phone" type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="(415) 555-1234" data-testid="account-phone-input" />
                  <Button onClick={requestPhoneChange} disabled={phoneSubmitting || !newPhone} data-testid="account-phone-submit" className="bg-[#0D9488] hover:bg-[#14B8A6]">
                    {phoneSubmitting ? "Sending…" : "Send code"}
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Enter the 6-digit code we texted to <strong>{newPhone}</strong>.</p>
                <Input inputMode="numeric" maxLength={6} value={phoneCode} onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="text-center tracking-widest text-lg" data-testid="account-phone-code-input" />
                <div className="flex gap-2">
                  <Button onClick={verifyPhoneChange} disabled={phoneSubmitting || phoneCode.length < 4} data-testid="account-phone-verify" className="flex-1 bg-[#0D9488] hover:bg-[#14B8A6]">
                    {phoneSubmitting ? "Verifying…" : "Verify & save"}
                  </Button>
                  <Button variant="outline" onClick={() => { setPhoneStep("idle"); setPhoneCode(""); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Password (reuse existing SecurityPanel) */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 pl-2">
            <KeyRound className="w-5 h-5 text-[#0D9488]" />
            <h2 className="text-lg font-semibold text-[#0A192F]">Password</h2>
          </div>
          <SecurityPanel />
        </div>

        {/* Danger zone */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700"><LogOut className="w-5 h-5" /> Sign out everywhere</CardTitle>
            <CardDescription>
              Revokes every active session AND every trusted device for your account. Use this if you lose a phone or think someone else got in.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={signOutEverywhere} disabled={signingOutAll} data-testid="signout-all-btn">
              {signingOutAll ? "Signing out…" : "Sign out of all devices"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AccountSettings;
